import { Component, inject, Inject } from '@angular/core';

import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ButtonComponent } from '@mds/ui';
import { ToastrService } from '@services/toaster.service';
import { EpdDashboardService } from '../../services/epd-dashboard.service';
import { collectChangedValues, triggerBlobDownload } from '../../utils/epd-dashboard-helpers';
import { EpdEditSectionComponent } from '../epd-edit-section/epd-edit-section.component';

@Component({
  selector: 'app-epd-export-modal',
  templateUrl: './epd-export-modal.component.html',
  styleUrls: ['./epd-export-modal.component.scss'],
  standalone: true,
  imports: [ButtonComponent, EpdEditSectionComponent],
  providers: [EpdDashboardService, ToastrService]
})
export class EpdExportModalComponent {
  protected form: any;
  protected values: any;
  protected payload = null;
  protected epdDashboardService = inject(EpdDashboardService);
  protected toastrService = inject(ToastrService);
  private initiateDownload = false;

  get exportButtonLabel() {
    return this.payload ? 'Save & Export' : 'Export';
  }

  get title() {
    return this.data.label || this.data.title;
  }

  get canExport() {
    return !!this.form?.valid;
  }

  get canUpdate() {
    return this.canExport && !!this.payload;
  }

  constructor(@Inject(MAT_DIALOG_DATA) protected data: any) {}

  onModelChanged(event) {
    if (!this.values) this.values = event.value;
    else {
      const updated = collectChangedValues(this.values, event.value);

      if (updated) {
        this.payload = updated;
      }
    }
    this.form = event;
  }

  onSave() {
    if (!this.form?.valid) return;

    this.epdDashboardService.submitData(this.data.rid, this.payload).subscribe((response) => {
      this.payload = null;
      if (this.initiateDownload) this.onExport();
      this.initiateDownload = false;
    });
  }

  onExport() {
    if (this.payload) {
      this.initiateDownload = true;
      this.onSave();
      return;
    }

    this.epdDashboardService.downloadAdditionalExports(this.data.url).subscribe(
      (response) => {
        triggerBlobDownload(response);
      },
      (error) => {
        void this.handleExportError(error);
      }
    );
  }

  private async handleExportError(error) {
    const message = await this.getExportErrorMessage(error);
    this.toastrService.show(message || 'Download failed', message ? 5000 : 3000);
  }

  private isExportValidationError(error) {
    return Boolean(error?.status === 400 && error?.error?.type === 'application/json');
  }

  private async getExportErrorMessage(error) {
    if (!this.isExportValidationError(error)) return null;

    try {
      const text = await error.error.text();
      return JSON.parse(text).message || null;
    } catch {
      return null;
    }
  }
}
